import {
  AutocompleteGooglePlaces,
  RealEstateDetailsSchema,
  RealEstate_InitialState,
  Select,
  TextInput,
  australianState,
  useAddress,
  useOptions
} from "@fla/components";
import HouseIcon from "@fla/components/src/assets/svgs/house.svg";
import { yupResolver } from "@hookform/resolvers/yup";
import { selectConsentOrdersBase } from "pages/ConsentOrders";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";

interface IProps {
  formId: string;
  store: RealEstate_InitialState;
  onSubmit: (values: RealEstate_InitialState) => void;
}

export default function Details({ formId, store, onSubmit }: IProps) {
  const { initialDetails } = useSelector(selectConsentOrdersBase);
  const { ownerV2Options } = useOptions({ initialDetails });

  const { handleSubmit, control, setValue } = useForm({
    mode: "all",
    defaultValues: store,
    resolver: yupResolver(RealEstateDetailsSchema)
  });

  const { handleNewLocation } = useAddress();
  const onSelectAddress = async (val: { label: string; value: string; __isNew__?: boolean }) => {
    if (val?.__isNew__) {
      setValue("street", val.value);
      return;
    }

    const address = await handleNewLocation(val.label);

    if (address?.locality) {
      setValue("suburb", address.locality);
    }

    if (address?.postalCode) {
      setValue("postcode", address.postalCode);
    }
    if (address?.state) {
      const state = australianState.find(v => v.label?.toLowerCase() === address.state?.toLowerCase())?.value;

      if (state) setValue("state", state);
    }
    if (address?.streetAddress) {
      setValue("street", address.streetAddress);
    }
  };

  return (
    <form id={formId} className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
      <div className="mb-8 pb-8 border-b flex flex-col items-center justify-center">
        <img src={HouseIcon} className="w-10 h-auto my-3" alt="real estate" />
        <h3 className="text-center">Real Estate</h3>
        <p className="text-sm">Details</p>
      </div>
      <AutocompleteGooglePlaces
        title={
          <div className="space-y-3">
            <p className="font-bold">Property Address</p>
            <p className="text-gray-500 font-semibold uppercase text-xs pb-4">Street Address</p>
          </div>
        }
        name="street"
        control={control}
        onSelectAddress={onSelectAddress}
      />

      <TextInput
        title="Suburb"
        titleClasses="text-gray-500 font-semibold uppercase text-xs"
        control={control}
        name="suburb"
        id="suburb"
        placeholder="Suburb"
      />

      <article className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
        <Select
          title="State"
          titleClasses="text-gray-500 font-semibold uppercase text-xs"
          control={control}
          name="state"
          id="state"
          placeholder="Select State"
          options={australianState}
          className="col-span-1"
        />
        <TextInput
          title="Postcode"
          titleClasses="text-gray-500 font-semibold uppercase text-xs"
          control={control}
          name="postcode"
          id="postcode"
          placeholder="Postcode"
          wrapClasses="col-span-1"
        />
      </article>
      <hr className="mt-7 mb-10 bg-gray-500 w-full" />
      <TextInput
        title="How much is this property worth?"
        subTitle="If you do not know the value, please enter in an estimate"
        control={control}
        name="value"
        id="value"
        placeholder="eg $500,000"
        isAmount
        wrapClasses="w-fit"
      />
      <hr className="mt-7 mb-10 bg-gray-500 w-full" />
      <Select
        title="Who currently owns the property?"
        subTitle="Select a name or option from the list"
        control={control}
        name="owner"
        id="owner"
        placeholder="Select"
        options={ownerV2Options}
        className="w-1/2"
      />
    </form>
  );
}
